import type { App, Ref } from 'vue';
import type { RouteRecordRaw } from 'vue-router';
import { ref, watch } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import DefaultLayout from './layouts/MainLayout/DefaultLayout.vue';
import type { AuthState } from '@dialpad/auth';
import type { Auth } from './auth';
import { NAVIGATION_TABS } from './constants';
import logger from './logger';

const log = logger.getChild('router');

const getRoutes = (auth: Auth): RouteRecordRaw[] => [
  {
    name: 'login',
    path: '/login',
    props: true,
    component: { template: '<div></div>' },
    beforeEnter(to): boolean {
      const redirectUrl =
        typeof to.params.redirectUrl === 'string' &&
        to.params.redirectUrl.length > 0
          ? to.params.redirectUrl
          : globalThis.location.origin;
      auth.redirectToLogin(redirectUrl);
      return false;
    },
  },
  {
    name: 'no-access',
    path: '/no-access',
    component: () => import('./app/components/NoAccess/NoAccess.vue'),
  },
  {
    name: 'loading',
    path: '/loading',
    props: true,
    component: { template: '<div></div>' }, // we could add some spinner here later
    beforeEnter: async (to, from, next) => {
      log.log('waiting for idb');
      await auth.authController.waitForIdb();
      log.log('idb ready');
      next((to.params.next as string) || '/');
    },
  },
  {
    path: '/',
    component: DefaultLayout,
    redirect: '/campaigns',
    children: [
      {
        name: 'campaigns',
        path: '/campaigns',
        component: () =>
          import('./campaign/components/CampaignList/CampaignList.vue'),
        meta: {
          tab: NAVIGATION_TABS.CAMPAIGNS,
        },
      },
      {
        name: 'csvs',
        path: '/lists',
        component: () => import('./csv/components/CsvList/CsvList.vue'),
        meta: {
          tab: NAVIGATION_TABS.LISTS,
        },
      },
      {
        name: 'campaign',
        path: '/campaign/:id',
        component: () =>
          import('./campaign/components/CampaignPage/CampaignPage.vue'),
        props: (route) => ({
          id: route.params.id,
        }),
        meta: {
          tab: NAVIGATION_TABS.CAMPAIGNS,
        },
      },
      {
        name: 'dnc',
        path: '/dnc',
        component: () => import('./dnc/components/DncList/DncList.vue'),
        meta: {
          tab: NAVIGATION_TABS.DNC,
        },
      },
    ],
  },
];

// This was lifted from app-core
// TODO: This should be refactored where we just inject auth into router
export function hostRouter(context: {
  env: { BASE_PATH: string };
  vueApp: App;
  auth: Auth;
}): void {
  log.log('hosting router');
  const { authController, maybeExchangeAuthCode } = context.auth;
  const router = createRouter({
    history: createWebHistory(context.env.BASE_PATH),
    routes: getRoutes(context.auth),
  });

  const authState: Ref<AuthState> = ref(authController.getAuthState());
  authController.subscribeToAuthState((state) => {
    authState.value = state;
  });

  // eslint-disable-next-line complexity
  router.beforeEach(async (to) => {
    log.log('router.beforeEach', to);
    let justAuthorized = false;
    const { data: authenticated, fetching } = authState.value;
    if (!authenticated) {
      // exchange auth code if present and not authorized yet
      justAuthorized = await maybeExchangeAuthCode();
      if (!justAuthorized && !fetching && to.name !== 'login') {
        log.log('redirecting to login');
        return {
          name: 'login',
          params: { redirectUrl: globalThis.location.toString() },
        };
      }
      if (!justAuthorized && fetching && to.name !== 'loading') {
        log.log('exchanging auth code, redirecting to loading');
        return { name: 'loading', params: { next: to.fullPath } };
      }
    }

    // it actually removes all hash params from url, so in case we decide to use in
    // we need to filter here code and state
    if (to.hash && (authenticated || justAuthorized)) {
      log.log('removing hash params');
      return {
        path: `${to.path}${globalThis.location.search}`,
        replace: true,
      };
    }

    return true;
  });

  watch([authState], ([state], [prevState]) => {
    if (!state.data && prevState.data) {
      log.log('Signed out, redirecting to login');
      return router.push({
        name: 'login',
        params: { redirectUrl: globalThis.location.toString() },
      });
    }
  });

  watch([authState], ([state], [prevState]) => {
    const { data: isAuthenticated, fetching: isFetching } = state;
    const { fetching: prevFetching } = prevState;

    // fetching true -> false, not authenticated, does not have auth code
    if (
      prevFetching &&
      !isFetching &&
      !isAuthenticated &&
      // we have to check real url here, but the good thing as we don't need
      // this prop to be reactive
      !globalThis.location.hash.includes('code')
    ) {
      log.log('Unauthorized, redirecting to login');
      return router.push({
        name: 'login',
        params: { redirectUrl: globalThis.location.toString() },
      });
    }
  });

  context.vueApp.use(router);
}
